import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Briefcase,
  FileText,
  Shield,
  Image as ImageIcon,
  LogOut,
} from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { Avatar } from '../ui/Avatar';
import { cn } from '../../lib/utils';
import logoColor from '../../assets/logo_color.png';

interface NavItem {
  label: string;
  href: string;
  icon: React.ReactNode;
}

export const adminNavItems: NavItem[] = [
  { label: 'Dashboard', href: '/admin/dashboard', icon: <LayoutDashboard className="w-5 h-5" /> },
  { label: 'Usuarios', href: '/admin/users', icon: <Users className="w-5 h-5" /> },
  { label: 'Trabajos', href: '/admin/jobs', icon: <Briefcase className="w-5 h-5" /> },
  { label: 'Categorías', href: '/admin/categories', icon: <FileText className="w-5 h-5" /> },
  { label: 'Reportes', href: '/admin/reports', icon: <Shield className="w-5 h-5" /> },
  { label: 'Banners', href: '/admin/banners', icon: <ImageIcon className="w-5 h-5" /> },
];

export const AdminSidebar: React.FC = () => {
  const { user, logout } = useAuthStore();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <aside className="hidden lg:flex flex-col w-64 h-screen sticky top-0 bg-cream2 border-r border-outlineVarWarm">
      <div className="flex items-center gap-2.5 px-5 h-16 border-b border-outlineVarWarm">
        <img src={logoColor} alt="Obros" className="w-8 h-8 object-contain" />
        <div className="leading-tight">
          <p className="text-sm font-bold text-onSurface tracking-tight">Obros</p>
          <p className="text-[11px] text-onSurface-mute">Panel administrativo</p>
        </div>
      </div>

      <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
        {adminNavItems.map((item) => {
          const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/');
          return (
            <Link
              key={item.href}
              to={item.href}
              className={cn(
                'relative flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-medium transition-colors',
                isActive
                  ? 'bg-primary-500 text-white shadow-sm'
                  : 'text-onSurface-var hover:bg-surface-var hover:text-onSurface'
              )}
            >
              {item.icon}
              {item.label}
            </Link>
          );
        })}
      </nav>

      <div className="p-3 border-t border-outlineVarWarm">
        <div className="flex items-center gap-2.5 px-2 py-2">
          <Avatar src={user?.avatar} name={user?.full_name} size="sm" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-onSurface truncate">{user?.full_name}</p>
            <p className="text-xs text-onSurface-mute truncate">{user?.email}</p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="mt-1 flex items-center gap-2.5 w-full px-3.5 py-2.5 rounded-xl text-sm font-medium text-danger-600 hover:bg-danger-50 transition-colors"
        >
          <LogOut className="w-4 h-4" /> Cerrar sesión
        </button>
      </div>
    </aside>
  );
};
